// LOAD-BEARING SIDE-EFFECT IMPORT — do not delete as "unused". See the
// `useQueryEhrLaunchPastNotes` delegate beside this file for the measurement.
import '@/pages/api/scribeAPIV2';

import { ehrRePairRequiredAtomFamily } from '@/atoms/integrations/integrationAtoms';
import { useOldFrameworkIntegrations } from '@/hooks/integrations/useOldFrameworkIntegrations';
import { formatToDateOnly } from '@/utils/dates';
import {
  getEhrConnectionErrorMessage,
  isEhrConnectionError,
  isEhrRePairRequiredError,
} from '@/utils/ehr-connection-error';
import { shouldSuppressDesktopOfflineNetworkErrorToast } from '@/utils/http/desktopOfflineNetwork';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { useQueryEHRAppointments as useQueryEHRAppointmentsCore } from '@heidi/integrations/ehr/data';
import { useTranslations } from '@heidi/runtime/i18n';
import { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useQueryUser } from '../useQueryUser';
import { useQueryUserEHR } from './useQueryUserEHR';

// Re-exported NAME-INTACT from the door that now declares it. Two app writes
// build tuples from it (`useMutateUpdateSessionEHRAppointmentId`,
// `useMutateUpdateSessionEHRDetails`), so the old specifier has to keep
// resolving.
export { GET_EHR_APPOINTMENTS } from '@heidi/integrations/ehr/contracts';

/**
 * Delegate, tier T2. The request, the cache identity, the `skipToken` gate and
 * the two refetch flags live at `@heidi/integrations/ehr/data`; the identity
 * terms, the re-pair atom and the error branch stay here.
 *
 * THE FOUR IDENTITY TERMS SPLIT TWO WAYS AND THAT SPLIT IS THE CARVE. `date` is
 * cache identity and crosses as the formatted string; the other three — a
 * signed-in user, a connected EHR profile, and that provider being an
 * OLD-FRAMEWORK integration — are resolved from two app queries plus an app
 * resolver and cross as one boolean.
 *
 * THE ATOM IS PER PROVIDER and stays that way, so a 409 on one EHR cannot affect
 * an unrelated one. Both writes keep their position: the clear fires between the
 * await and the return, the set fires as the first arm of the catch.
 *
 * `re-pair required` DELIBERATELY DOES NOT GATE THE FETCH. It disables the
 * focus/reconnect refetch storm while leaving mount, date-nav and explicit retry
 * runnable, which is what lets a successful fetch self-clear the flag rather
 * than locking the surface out permanently.
 *
 * THE AXIOS ARM NEVER TOASTS THE PAYLOAD. EHR responses can carry PHI/PII and
 * the Datadog channel has no `beforeSend` scrubbing, so the log carries four
 * named fields and the toast is fixed copy. That constraint is the reason the
 * whole branch stays app-side: the package throws an argument-less `Error`, so
 * nothing downstream can render a backend string even by accident.
 *
 * Signature unchanged: callers still pass `(date?)`.
 */
export function useQueryEHRAppointments(date?: Date) {
  const { t } = useTranslations();
  const { data: user } = useQueryUser((data) => data?.kinde_user_id);
  const { data: userEhrProfile } = useQueryUserEHR((data) => data?.ehr_profile);
  const oldFrameworkIntegrations = useOldFrameworkIntegrations();
  // Re-pair state is per-provider so a 409 here can't affect an unrelated EHR/surface.
  const [isRePairRequired, setRePairRequired] = useAtom(
    ehrRePairRequiredAtomFamily(userEhrProfile?.ehr_provider ?? '')
  );

  const dateInput = formatToDateOnly(date) ?? undefined;

  return useQueryEHRAppointmentsCore({
    date: dateInput,
    canFetchAppointments: Boolean(
      !!user &&
      !!userEhrProfile &&
      oldFrameworkIntegrations.includes(userEhrProfile.ehr_provider)
    ),
    // While re-pairing is required, stop the focus/reconnect refetch storm. We keep
    // refetch-on-mount, date-nav and explicit retry alive so a successful fetch after
    // the user re-pairs self-clears the flag (no permanent lock-out).
    refetchOnFocusAndReconnect: !isRePairRequired,
    onAppointmentsFetched: () => {
      // A healthy response clears any prior re-pair-required state.
      setRePairRequired(false);
    },
    onAppointmentsFetchError: (error) => {
      if (isEhrRePairRequiredError(error)) {
        // Recoverable: drive the reconnect banner instead of toasting the raw error.
        setRePairRequired(true);
      } else if (isEhrConnectionError(error)) {
        toast({
          tone: 'destructive',
          title: t('ehr.ehrConnectionError'),
          description: getEhrConnectionErrorMessage(error),
        });
      } else if (error instanceof AxiosError) {
        // Never toast the raw payload — EHR responses can carry PHI/PII.
        // Log only sanitised, non-sensitive fields: the full AxiosError
        // carries response.data (PHI) and config.headers.Authorization
        // (bearer token), and the Datadog channel has no beforeSend
        // scrubbing. The toast shows a fixed, safe message.
        logger.error('EHR appointments request failed', {
          message: error.message,
          status: error.response?.status,
          url: error.config?.url,
          method: error.config?.method,
        });
        if (!shouldSuppressDesktopOfflineNetworkErrorToast(error)) {
          toast({
            tone: 'destructive',
            title: t('ehr.ehrConnectionError'),
            description: t('ehr.pleaseTryAgain'),
          });
        }
      }
    },
  });
}
